import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../../context/LanguageContext';
import { loadConsent, saveConsent, purgeTrackingCookies, type CookiePreferences } from '../../../../utils/CookieConsent';
import '../style/cookie-consent.css';

const CookieConsent: React.FC = () => {
    const { language } = useLanguage();
    const [showPopup, setShowPopup] = useState(false);
    const [showCustomize, setShowCustomize] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        essential: true,
        analytics: false,
        marketing: false,
        preferences: false
    });

    useEffect(() => {
        const existing = loadConsent();
        if (existing === null) {
            // No valid consent record → show banner. wait_for_update (2000ms
            // in index.html) covers the entire visible flow before tags fire.
            setShowPopup(true);
        }
        // If non-null, the index.html restore script already called
        // gtag('consent','update', ...). Banner stays hidden.

        // Footer "Definições de Cookies" link: reopen with the saved choices
        // pre-loaded and the category panel open, so withdrawing consent is
        // as easy as giving it (GDPR art. 7(3)).
        const reopen = () => {
            const saved = loadConsent();
            if (saved) setPreferences(saved);
            setShowCustomize(true);
            setShowPopup(true);
        };
        window.addEventListener('openCookieSettings', reopen);
        return () => window.removeEventListener('openCookieSettings', reopen);
    }, []);

    const handleAcceptAll = () => {
        const allAccepted: CookiePreferences = {
            essential: true,
            analytics: true,
            marketing: true,
            preferences: true
        };
        persistChoice(allAccepted);
    };

    const handleDeclineAll = () => {
        const declined: CookiePreferences = {
            essential: true, // Essential is always true
            analytics: false,
            marketing: false,
            preferences: false
        };
        persistChoice(declined);
    };

    const handleSaveCustom = () => {
        persistChoice(preferences);
    };

    const persistChoice = (consentPreferences: CookiePreferences) => {
        // Downgrade detection BEFORE saving: any category flipping granted→denied.
        const previous = loadConsent();
        const downgraded = !!previous && (['analytics', 'marketing', 'preferences'] as const)
            .some((k) => previous[k] && !consentPreferences[k]);

        saveConsent(consentPreferences); // writes localStorage v1 record + gtag update + consent-log evidence
        setShowPopup(false);
        setShowCustomize(false);

        // Keep the existing event so any legacy listeners continue to work
        window.dispatchEvent(new CustomEvent('cookieConsentGiven', {
            detail: { preferences: consentPreferences }
        }));

        if (downgraded) {
            // Consent Mode stops future Google writes, but already-loaded
            // scripts (Meta pixel) keep running and existing cookies stay on
            // the device. Purge + reload makes the withdrawal real.
            purgeTrackingCookies();
            window.location.reload();
        }
    };

    const handleToggleCustomize = () => {
        setShowCustomize(!showCustomize);
    };

    const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
        if (key === 'essential') return; // Essential is always true
        setPreferences(prev => ({
            ...prev,
            [key]: value
        }));
    };

    if (!showPopup) return null;

    const translations = {
        cookiePreferences: {
            pt: 'Preferências de Cookies',
            en: 'Cookie Preferences'
        },
        cookieDescription: {
            pt: 'Utilizamos cookies para melhorar a sua experiência de navegação, fornecer conteúdo personalizado e analisar o nosso tráfego. Pode aceitar todos os cookies, recusar todos ou personalizar as suas escolhas por categoria. Pode alterar ou retirar o seu consentimento a qualquer momento em "Definições de Cookies", no rodapé.',
            en: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. You can accept all cookies, decline all, or customize your choices by category. You can change or withdraw your consent at any time via "Cookie Settings" in the footer.'
        },
        acceptAll: {
            pt: 'Aceitar Tudo',
            en: 'Accept All'
        },
        declineAll: {
            pt: 'Recusar Tudo',
            en: 'Decline All'
        },
        customize: {
            pt: 'Personalizar',
            en: 'Customize'
        },
        essentialCookies: {
            pt: 'Cookies Essenciais',
            en: 'Essential Cookies'
        },
        analyticsCookies: {
            pt: 'Cookies de Análise',
            en: 'Analytics Cookies'
        },
        marketingCookies: {
            pt: 'Cookies de Marketing',
            en: 'Marketing Cookies'
        },
        preferencesCookies: {
            pt: 'Cookies de Preferências',
            en: 'Preference Cookies'
        },
        savePreferences: {
            pt: 'Guardar Preferências',
            en: 'Save Preferences'
        },
        privacyPolicy: {
            pt: 'Política de Privacidade',
            en: 'Privacy Policy'
        },
        termsConditions: {
            pt: 'Termos e Condições',
            en: 'Terms & Conditions'
        },
        cookiePolicy: {
            pt: 'Política de Cookies',
            en: 'Cookie Policy'
        }
    };

    const t = (key: keyof typeof translations) => translations[key][language] || translations[key]['pt'];

    return (
        <div className={`cookie-consent-popup ${showPopup ? 'show' : ''}`}>
            <div className="cookie-consent-header">
                <div className="cookie-consent-title">
                    <div className="cookie-consent-icon">
                        <i className="fas fa-cookie-bite"></i>
                    </div>
                    <h4>{t('cookiePreferences')}</h4>
                </div>
            </div>
            <div className="cookie-consent-content">
                <div className="cookie-consent-text">
                    {t('cookieDescription')}
                </div>
                
                <div className="cookie-consent-buttons">
                    <button 
                        className="cookie-btn cookie-btn-accept" 
                        onClick={handleAcceptAll}
                    >
                        {t('acceptAll')}
                    </button>
                    <button 
                        className="cookie-btn cookie-btn-decline" 
                        onClick={handleDeclineAll}
                    >
                        {t('declineAll')}
                    </button>
                    <button 
                        className="cookie-btn cookie-btn-customize" 
                        onClick={handleToggleCustomize}
                    >
                        {t('customize')}
                    </button>
                </div>
                
                <div className={`cookie-consent-customize ${showCustomize ? 'show' : ''}`}>
                    <div className="cookie-consent-options">
                        <div className="cookie-option">
                            <label htmlFor="essential-cookies">
                                {t('essentialCookies')}
                            </label>
                            <input 
                                type="checkbox" 
                                id="essential-cookies" 
                                checked={true}
                                disabled
                            />
                        </div>
                        <div className="cookie-option">
                            <label htmlFor="analytics-cookies">
                                {t('analyticsCookies')}
                            </label>
                            <input 
                                type="checkbox" 
                                id="analytics-cookies"
                                checked={preferences.analytics}
                                onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                            />
                        </div>
                        <div className="cookie-option">
                            <label htmlFor="marketing-cookies">
                                {t('marketingCookies')}
                            </label>
                            <input 
                                type="checkbox" 
                                id="marketing-cookies"
                                checked={preferences.marketing}
                                onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                            />
                        </div>
                        <div className="cookie-option">
                            <label htmlFor="preferences-cookies">
                                {t('preferencesCookies')}
                            </label>
                            <input 
                                type="checkbox" 
                                id="preferences-cookies"
                                checked={preferences.preferences}
                                onChange={(e) => handlePreferenceChange('preferences', e.target.checked)}
                            />
                        </div>
                    </div>
                    <div className="cookie-consent-buttons">
                        <button 
                            className="cookie-btn cookie-btn-accept" 
                            onClick={handleSaveCustom}
                        >
                            {t('savePreferences')}
                        </button>
                    </div>
                </div>
                
                <div className="cookie-consent-links">
                    <a href="/politica-privacidade">{t('privacyPolicy')}</a>
                    <a href="/termos-condicoes">{t('termsConditions')}</a>
                    <a href="/politica-cookies">{t('cookiePolicy')}</a>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;

